/* Power Grips: dispatch countdown + delivery estimate. Shared date logic for both. */
(() => {
  if (window.PgDispatch) return;

  const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const DAY_MS = 86400000;
  const formatters = {};

  const pad = (n) => String(n).padStart(2, '0');
  const addDays = (date, n) => new Date(date.getTime() + n * DAY_MS);
  const iso = (date) => date.toISOString().slice(0, 10);
  const dayDiff = (a, b) => Math.round((a.getTime() - b.getTime()) / DAY_MS);

  const parseDays = (value) =>
    (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
      .map(Number);

  const parseDates = (value) =>
    new Set(
      (value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    );

  /* Today's calendar date and seconds since midnight on the shop's clock, whatever the visitor's timezone. */
  function shopNow(tz) {
    if (!formatters[tz]) {
      formatters[tz] = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    }
    const parts = {};
    formatters[tz].formatToParts(new Date()).forEach((part) => {
      parts[part.type] = part.value;
    });
    return {
      date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
      seconds: (Number(parts.hour) % 24) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
    };
  }

  /* First day strictly after `from` that is one of `days` and not a closed date. */
  function nextDay(from, days, closed) {
    let date = from;
    for (let i = 0; i < 30; i += 1) {
      date = addDays(date, 1);
      if (days.includes(date.getUTCDay()) && !closed.has(iso(date))) return date;
    }
    return null;
  }

  function schedule(options) {
    let now;
    try {
      now = shopNow(options.tz || 'Europe/Dublin');
    } catch (error) {
      return null;
    }
    if (!options.shipDays.length || !options.deliveryDays.length) return null;

    const cutoffSeconds = options.cutoff * 3600;
    /* After the cutoff, an order counts as placed the next day. */
    const orderDay = now.seconds < cutoffSeconds ? now.date : addDays(now.date, 1);
    const ship = nextDay(orderDay, options.shipDays, options.closed);
    if (!ship) return null;

    /* Deadline: the last evening an order can be placed and still go out on that ship date. */
    let deadline = orderDay;
    for (let i = 0; i < 30; i += 1) {
      const candidate = addDays(deadline, 1);
      const candidateShip = nextDay(candidate, options.shipDays, options.closed);
      if (!candidateShip || candidateShip.getTime() !== ship.getTime()) break;
      deadline = candidate;
    }

    const minDays = Math.max(0, options.minDays);
    const maxDays = Math.max(minDays, options.maxDays);
    let from = ship;
    for (let i = 0; i < minDays; i += 1) {
      from = nextDay(from, options.deliveryDays, options.closed);
      if (!from) return null;
    }
    let to = from;
    for (let i = minDays; i < maxDays; i += 1) {
      to = nextDay(to, options.deliveryDays, options.closed);
      if (!to) return null;
    }

    const daysToDeadline = dayDiff(deadline, now.date);
    return {
      ship,
      deadline,
      from,
      to,
      deadlineIsToday: daysToDeadline === 0,
      secondsLeft: daysToDeadline * 86400 + cutoffSeconds - now.seconds,
    };
  }

  const shortDate = (date) => DAY_SHORT[date.getUTCDay()] + ' ' + date.getUTCDate() + ' ' + MONTH_SHORT[date.getUTCMonth()];

  function dateRange(from, to) {
    if (from.getTime() === to.getTime()) return shortDate(from);
    if (from.getUTCMonth() === to.getUTCMonth()) {
      return DAY_SHORT[from.getUTCDay()] + ' ' + from.getUTCDate() + ' – ' + shortDate(to);
    }
    return shortDate(from) + ' – ' + shortDate(to);
  }

  function fill(template, result, timeLabel) {
    return (template || '')
      .replace('[time]', timeLabel || '')
      .replace('[day]', DAY[result.deadline.getUTCDay()])
      .replace('[ship_day]', DAY[result.ship.getUTCDay()])
      .replace('[ship_date]', shortDate(result.ship))
      .replace('[delivery]', dateRange(result.from, result.to));
  }

  function readOptions(element) {
    const data = element.dataset;
    const cutoff = parseInt(data.cutoff, 10);
    return {
      tz: data.tz || 'Europe/Dublin',
      /* A cutoff of 0 means midnight at the end of the day, so treat it as hour 24. */
      cutoff: cutoff > 0 ? cutoff : 24,
      shipDays: parseDays(data.shipDays),
      deliveryDays: parseDays(data.deliveryDays || '1,2,3,4,5'),
      closed: parseDates(data.closed),
      minDays: parseInt(data.minDays || '1', 10),
      maxDays: parseInt(data.maxDays || '2', 10),
    };
  }

  function setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  class PgDispatchCountdown extends HTMLElement {
    connectedCallback() {
      this.options = readOptions(this);
      this.titleEl = this.querySelector('[data-title]');
      this.subEl = this.querySelector('[data-sub]');
      this.timerEl = this.querySelector('[data-timer]');
      this.daysUnit = this.querySelector('[data-days-unit]');
      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minsEl = this.querySelector('[data-mins]');
      this.secsEl = this.querySelector('[data-secs]');
      if (!this.timerEl) return;
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    tick() {
      const result = schedule(this.options);
      if (!result) return;

      const today = result.deadlineIsToday;
      const template = today ? this.dataset.textToday : this.dataset.textLater;
      setText(this.titleEl, fill(template, result, this.dataset.timeLabel));
      this.dataset.state = today ? 'today' : 'later';

      if (this.subEl && this.dataset.textSub) {
        setText(this.subEl, fill(this.dataset.textSub, result, this.dataset.timeLabel));
        this.subEl.hidden = false;
      }

      /* Once tonight's cutoff has passed, the clock can be hidden: a 23-hour countdown is not urgent. */
      if (!today && this.dataset.hideTimerLater === 'true') {
        this.timerEl.hidden = true;
        return;
      }

      const left = Math.max(0, result.secondsLeft);
      const days = Math.floor(left / 86400);
      this.daysUnit.hidden = days === 0;
      this.daysEl.textContent = String(days);
      this.hoursEl.textContent = pad(Math.floor((left % 86400) / 3600));
      this.minsEl.textContent = pad(Math.floor((left % 3600) / 60));
      this.secsEl.textContent = pad(left % 60);
      this.timerEl.hidden = false;
    }
  }

  class PgDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.options = readOptions(this);
      this.titleEl = this.querySelector('[data-title]');
      this.subEl = this.querySelector('[data-sub]');
      this.update();
      this.interval = setInterval(() => this.update(), 20000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    update() {
      const result = schedule(this.options);
      if (!result) return;

      const template = result.deadlineIsToday ? this.dataset.textToday : this.dataset.textLater;
      setText(this.titleEl, fill(template, result, this.dataset.timeLabel));

      if (this.subEl && this.dataset.textSub) {
        setText(this.subEl, fill(this.dataset.textSub, result, this.dataset.timeLabel));
        this.subEl.hidden = false;
      }
    }
  }

  window.PgDispatch = { schedule, fill, dateRange, shortDate };

  if (!customElements.get('pg-dispatch-countdown')) {
    customElements.define('pg-dispatch-countdown', PgDispatchCountdown);
  }
  if (!customElements.get('pg-delivery-estimate')) {
    customElements.define('pg-delivery-estimate', PgDeliveryEstimate);
  }
})();
